<template>
  <div class="page-default">
    <div class="map-section">
      <div class="dashboard-map">
        <div class="map-body">
          <div class="mouseTrigger">
            <div class="map-filter-defects">
              <div class="trams-filter position-relative" ref="filterRoot">
                <button
                  type="button"
                  class="btn btn-dark d-flex align-items-center"
                  :disabled="isLoadingAvailability || !availability"
                  @click="toggleFilter"
                >
                  <span v-if="isLoadingAvailability">Loading…</span>
                  <span v-else-if="!hasSelection">Select PCA data</span>
                  <span v-else>{{ summaryLabel }}</span>
                  <font-awesome-icon
                    :icon="['fas', isFilterOpen ? 'angle-up' : 'angle-down']"
                    class="ml-2"
                  />
                </button>

                <div
                  v-if="isFilterOpen"
                  class="trams-filter-popover bg-dashboard-dark-2 rounded shadow p-3"
                >
                  <div
                    class="trams-matrix-container"
                    @wheel="onYearWheel"
                  >
                    <div class="trams-districts-col">
                      <div class="trams-cell trams-header-cell"></div>
                      <div
                        v-for="district in districts"
                        :key="district.id"
                        class="trams-cell trams-district-label"
                      >
                        {{ district.name }}
                      </div>
                    </div>

                    <button
                      v-if="sortedYears.length > yearWindowSize"
                      type="button"
                      class="trams-chevron-btn"
                      :disabled="!canScrollYearsLeft"
                      @click="scrollYearsLeft"
                      title="Older years"
                    >
                      <font-awesome-icon :icon="['fas', 'chevron-left']" />
                    </button>

                    <div
                      class="trams-year-viewport"
                      :style="yearViewportStyle"
                    >
                      <div
                        class="trams-year-scroller"
                        :style="yearScrollerStyle"
                      >
                        <div
                          v-for="year in sortedYears"
                          :key="year"
                          class="trams-year-col"
                        >
                          <div class="trams-cell trams-header-cell">
                            {{ year }}
                          </div>
                          <div
                            v-for="district in districts"
                            :key="district.id"
                            class="trams-cell year-cell"
                            :class="cellClass(district, year)"
                            @click="onCellClick(district, year)"
                          >
                            <font-awesome-icon
                              v-if="
                                selectedDistrict &&
                                selectedDistrict.id === district.id &&
                                selectedYear === year
                              "
                              :icon="['fas', 'check']"
                              class="cell-check"
                            />
                            <span
                              v-else-if="isCellAvailable(district.id, year)"
                              class="dot"
                            ></span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      v-if="sortedYears.length > yearWindowSize"
                      type="button"
                      class="trams-chevron-btn"
                      :disabled="!canScrollYearsRight"
                      @click="scrollYearsRight"
                      title="Newer years"
                    >
                      <font-awesome-icon :icon="['fas', 'chevron-right']" />
                    </button>

                    <div
                      class="trams-resize-handle"
                      :class="{ resizing: isResizing }"
                      title="Drag to resize"
                      @mousedown="onResizeStart"
                    ></div>
                  </div>

                  <div class="d-flex align-items-center mt-3">
                    <span class="text-white-50 small mr-2">Type:</span>
                    <div class="btn-group btn-group-sm flex-wrap">
                      <button
                        v-for="t in types"
                        :key="t"
                        type="button"
                        class="btn"
                        :class="
                          selectedType === t
                            ? 'btn-primary'
                            : 'btn-outline-light'
                        "
                        :disabled="!isTypeAvailableForCurrentCell(t)"
                        @click="onTypeClick(t)"
                      >
                        {{ typeLabel(t) }}
                      </button>
                    </div>
                    <font-awesome-icon
                      :icon="['fas', 'question-circle']"
                      class="ml-auto trams-help-icon"
                      data-toggle="tooltip"
                      data-placement="left"
                      :title="helpText"
                      @mouseover="helper.showTooltip"
                      @mouseleave="helper.hideTooltip"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div id="map" class="bg-dashboard-dark-2"></div>

            <div
              v-if="errorMessage"
              class="trams-error-banner bg-danger text-white px-3 py-2 rounded shadow"
            >
              {{ errorMessage }}
              <button
                type="button"
                class="btn btn-sm btn-outline-light ml-2"
                @click="retry"
              >
                Retry
              </button>
            </div>

            <div
              v-if="
                !isProcessing &&
                !errorMessage &&
                hasSelection &&
                listOfTrams.length === 0
              "
              class="trams-empty-banner bg-dark text-white-50 px-3 py-2 rounded shadow"
            >
              No PCA data for {{ selectedDistrict.name }} in {{ selectedYear }}.
            </div>

            <div
              class="btn map-legend cursor-pointer"
              data-toggle="tooltip"
              data-placement="top"
              title="Legend"
              @mouseover="helper.showTooltip"
              @mouseleave="helper.hideTooltip"
              :class="{ expand: expandLegend }"
              @click="expandLegend = !expandLegend"
            >
              <p
                class="px-3 font-weight-medium mb-0 d-flex justify-content-between align-items-center text-white"
              >
                Legend
                <font-awesome-icon
                  class="text-white angle-icon ml-2"
                  :icon="['fas', 'angle-down']"
                />
              </p>
              <div
                class="legend-body d-flex flex-column justify-content-start align-items-start text-white"
              >
                <div class="legend-item">
                  <div
                    class="defect-marker mr-2"
                    style="background-color: green"
                  ></div>
                  Good
                </div>
                <div class="legend-item">
                  <div
                    class="defect-marker mr-2"
                    style="background-color: blue"
                  ></div>
                  Fair
                </div>
                <div class="legend-item">
                  <div
                    class="defect-marker mr-2"
                    style="background-color: yellow"
                  ></div>
                  Poor
                </div>
                <div class="legend-item">
                  <div
                    class="defect-marker mr-2"
                    style="background-color: red"
                  ></div>
                  Bad
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <spinner v-if="isProcessing" />
  </div>
</template>
<script>
import API from "@/api";
import "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import axios from "axios";
import Spinner from "@/components/Spinner";
import Helper from "@/helper";

const SELECTION_STORAGE_KEY = "trams.selection";

export default {
  name: "Trams",
  components: {
    spinner: Spinner,
  },
  data() {
    return {
      helper: Helper,
      popup: null,
      isProcessing: false,
      isLoadingAvailability: true,
      availability: null,
      selectedType: null,
      selectedDistrict: null,
      selectedYear: null,
      listOfTrams: [],
      map: null,
      expandLegend: true,
      isFilterOpen: false,
      errorMessage: null,
      refreshTimer: null,
      tramsAbortController: null,
      yearWindowStart: 0,
      yearWindowSize: this.readPersistedYearWindowSize(),
      isResizing: false,
      resizeStartX: 0,
      resizeStartSize: 0,
      resizeEndTime: 0,
    };
  },
  computed: {
    sortedYears() {
      const ys = this.availability?.years || [];
      if (ys.length === 0) return [];
      const min = Math.min(...ys);
      const max = Math.max(...ys);
      const filled = [];
      for (let y = min; y <= max; y++) filled.push(y);
      return filled;
    },
    visibleYears() {
      return this.sortedYears.slice(
        this.yearWindowStart,
        this.yearWindowStart + this.yearWindowSize
      );
    },
    canScrollYearsLeft() {
      return this.yearWindowStart > 0;
    },
    canScrollYearsRight() {
      return (
        this.yearWindowStart + this.yearWindowSize < this.sortedYears.length
      );
    },
    visibleColCount() {
      return Math.min(this.yearWindowSize, this.sortedYears.length);
    },
    yearViewportStyle() {
      return { width: `${this.visibleColCount * 64}px` };
    },
    yearScrollerStyle() {
      return {
        transform: `translateX(${-this.yearWindowStart * 64}px)`,
      };
    },
    helpText() {
      let text = "Filled cells have data.";
      if (this.sortedYears.length > this.yearWindowSize) {
        text += " Shift + scroll to pan years.";
      }
      return text;
    },
    types() {
      return this.availability?.types || [];
    },
    districts() {
      const all = this.availability?.districts || [];
      const userRaw = localStorage.getItem("user");
      if (userRaw) {
        try {
          const user = JSON.parse(userRaw);
          if (user.districts && user.districts.length > 0) {
            const allowed = new Set(user.districts.map((d) => d.name));
            return all.filter((d) => allowed.has(d.name));
          }
        } catch (e) {
          // fall through to unfiltered
        }
      }
      return all;
    },
    availabilityMatrix() {
      const m = {};
      const cells = this.availability?.cells || [];
      for (const cell of cells) {
        if (!m[cell.district]) m[cell.district] = {};
        m[cell.district][cell.year] = cell.types;
      }
      return m;
    },
    typesForCurrentCell() {
      if (!this.selectedDistrict || !this.selectedYear) return [];
      return (
        this.availabilityMatrix[this.selectedDistrict.id]?.[this.selectedYear] ||
        []
      );
    },
    hasSelection() {
      return !!(
        this.selectedDistrict &&
        this.selectedYear &&
        this.selectedType
      );
    },
    summaryLabel() {
      if (!this.hasSelection) return "";
      return `${this.selectedDistrict.name} · ${this.selectedYear} · ${this.typeLabel(
        this.selectedType
      )}`;
    },
    filterTriple() {
      return [
        this.selectedDistrict?.id || "",
        this.selectedYear || "",
        this.selectedType || "",
      ].join("|");
    },
  },
  watch: {
    filterTriple() {
      this.scheduleRefresh();
    },
  },
  methods: {
    typeLabel(value) {
      return value.replace(/_/g, " ");
    },
    isCellAvailable(districtId, year) {
      return !!this.availabilityMatrix[districtId]?.[year];
    },
    isTypeAvailableForCurrentCell(type) {
      return this.typesForCurrentCell.includes(type);
    },
    cellClass(district, year) {
      const available = this.isCellAvailable(district.id, year);
      const selected =
        this.selectedDistrict?.id === district.id &&
        this.selectedYear === year;
      return {
        available,
        unavailable: !available,
        selected,
      };
    },
    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen;
      if (this.isFilterOpen) {
        if (this.selectedYear) {
          this.snapYearWindowTo(this.selectedYear);
        } else {
          this.snapYearWindowToLatest();
        }
      }
    },
    scrollYearsLeft() {
      this.yearWindowStart = Math.max(0, this.yearWindowStart - 1);
    },
    scrollYearsRight() {
      const maxStart = Math.max(
        0,
        this.sortedYears.length - this.yearWindowSize
      );
      this.yearWindowStart = Math.min(maxStart, this.yearWindowStart + 1);
    },
    snapYearWindowToLatest() {
      this.yearWindowStart = Math.max(
        0,
        this.sortedYears.length - this.yearWindowSize
      );
    },
    readPersistedYearWindowSize() {
      try {
        const raw = localStorage.getItem("trams.yearWindowSize");
        if (raw) {
          const v = parseInt(raw, 10);
          if (!isNaN(v) && v >= 3 && v <= 20) return v;
        }
      } catch (e) {
        // ignore
      }
      return 5;
    },
    persistYearWindowSize() {
      try {
        localStorage.setItem(
          "trams.yearWindowSize",
          String(this.yearWindowSize)
        );
      } catch (e) {
        // ignore
      }
    },
    onResizeStart(e) {
      this.isResizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartSize = this.yearWindowSize;
      document.addEventListener("mousemove", this.onResizeMove);
      document.addEventListener("mouseup", this.onResizeEnd);
      document.body.style.cursor = "ew-resize";
      e.preventDefault();
    },
    onResizeMove(e) {
      if (!this.isResizing) return;
      const colWidth = 64;
      const delta = e.clientX - this.resizeStartX;
      const maxSize = Math.max(1, this.sortedYears.length);
      const minSize = Math.min(3, maxSize);
      const newSize = Math.max(
        minSize,
        Math.min(maxSize, this.resizeStartSize + Math.round(delta / colWidth))
      );
      if (newSize === this.yearWindowSize) return;
      this.yearWindowSize = newSize;
      const maxStart = Math.max(0, this.sortedYears.length - newSize);
      if (this.yearWindowStart > maxStart) this.yearWindowStart = maxStart;
    },
    onResizeEnd() {
      this.isResizing = false;
      this.resizeEndTime = Date.now();
      document.removeEventListener("mousemove", this.onResizeMove);
      document.removeEventListener("mouseup", this.onResizeEnd);
      document.body.style.cursor = "";
      this.persistYearWindowSize();
    },
    onYearWheel(e) {
      let delta = e.deltaX;
      if (delta === 0 && e.shiftKey) delta = e.deltaY;
      if (delta === 0) return;
      e.preventDefault();
      const now = Date.now();
      if (this._lastWheelTime && now - this._lastWheelTime < 180) return;
      this._lastWheelTime = now;
      if (delta > 0) this.scrollYearsRight();
      else this.scrollYearsLeft();
    },
    snapYearWindowTo(year) {
      const idx = this.sortedYears.indexOf(year);
      if (idx < 0) return;
      if (idx < this.yearWindowStart) {
        this.yearWindowStart = idx;
      } else if (idx >= this.yearWindowStart + this.yearWindowSize) {
        this.yearWindowStart = Math.max(0, idx - this.yearWindowSize + 1);
      }
    },
    onCellClick(district, year) {
      if (!this.isCellAvailable(district.id, year)) return;
      this.selectedDistrict = district;
      this.selectedYear = year;
      if (!this.typesForCurrentCell.includes(this.selectedType)) {
        this.selectedType = this.typesForCurrentCell[0];
      }
      this.isFilterOpen = false;
      this.persistSelection();
    },
    onTypeClick(type) {
      if (!this.isTypeAvailableForCurrentCell(type)) return;
      this.selectedType = type;
      this.persistSelection();
    },
    persistSelection() {
      if (!this.hasSelection) return;
      try {
        localStorage.setItem(
          SELECTION_STORAGE_KEY,
          JSON.stringify({
            districtId: this.selectedDistrict.id,
            year: this.selectedYear,
            type: this.selectedType,
          })
        );
      } catch (e) {
        // localStorage full / disabled — silently ignore
      }
    },
    readPersistedSelection() {
      try {
        const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    pickDefaultSelection() {
      const persisted = this.readPersistedSelection();
      if (persisted) {
        const district = this.districts.find(
          (d) => d.id === persisted.districtId
        );
        if (district && this.isCellAvailable(district.id, persisted.year)) {
          const cellTypes =
            this.availabilityMatrix[district.id][persisted.year];
          const type = cellTypes.includes(persisted.type)
            ? persisted.type
            : cellTypes[0];
          return { district, year: persisted.year, type };
        }
      }
      for (const district of this.districts) {
        const cellsForDistrict = this.availabilityMatrix[district.id];
        if (!cellsForDistrict) continue;
        const yearsWithData = Object.keys(cellsForDistrict)
          .map(Number)
          .sort((a, b) => b - a);
        if (yearsWithData.length === 0) continue;
        const year = yearsWithData[0];
        const cellTypes = cellsForDistrict[year];
        return { district, year, type: cellTypes[0] };
      }
      return null;
    },
    applySelection(sel) {
      this.selectedDistrict = sel.district;
      this.selectedYear = sel.year;
      this.selectedType = sel.type;
    },
    async fetchAvailability() {
      this.isLoadingAvailability = true;
      this.errorMessage = null;
      try {
        const res = await API.get("trams/availability");
        this.availability = res.data;
      } catch (err) {
        this.errorMessage =
          "Failed to load PCA filter data. Check connection and retry.";
        throw err;
      } finally {
        this.isLoadingAvailability = false;
      }
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.refresh();
      }, 100);
    },
    async refresh() {
      if (!this.hasSelection) return;
      await this.removeLine();
      await this.getTrams();
      if (this.map) this.tripMouseLeaveAction();
    },
    retry() {
      this.errorMessage = null;
      if (!this.availability) {
        this.fetchAvailability()
          .then(() => {
            const sel = this.pickDefaultSelection();
            if (sel) this.applySelection(sel);
          })
          .catch(() => {});
      } else {
        this.refresh();
      }
    },
    removeLine() {
      if (!this.map) return;
      const name_source = `trams-`;
      const layers = this.map
        .getStyle()
        .layers.filter((layer) => layer.id.includes(name_source));

      const sources = Object.keys(this.map.getStyle().sources).filter(
        (source) => source.includes(name_source)
      );

      if (layers && layers.length > 0) {
        layers.forEach((layer) => {
          this.map.removeLayer(layer.id);
        });
      }

      if (sources && sources.length > 0) {
        sources.forEach((source) => {
          this.map.removeSource(source);
        });
      }
    },
    getTrams() {
      this.isProcessing = true;
      this.errorMessage = null;

      if (this.tramsAbortController) {
        this.tramsAbortController.abort();
      }
      this.tramsAbortController = new AbortController();

      const url = `trams?_sort=name&year=${this.selectedYear}&type=${this.selectedType}&_where[district]=${this.selectedDistrict.id}`;

      return API.get(url, null, { signal: this.tramsAbortController.signal })
        .then((res) => {
          this.isProcessing = false;
          this.listOfTrams = res.data;
          this.drawline();
        })
        .catch((err) => {
          if (axios.isCancel(err) || err.name === "CanceledError") return;
          this.isProcessing = false;
          this.errorMessage = "Failed to load PCA data.";
        });
    },
    tripMouseClickAction(e) {
      if (this.popup) {
        const coordinates = e.features[0].geometry.coordinates.slice();

        const district = e.features[0].properties.name;
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        let popupContainer = `
        <div class="mapbox-popup-container rounded border-0 shadow">
          <div class="popup-header text-left line-height-1 border-0" style="padding: 10px">
            <div class="d-flex">
                <div class="defect-marker mr-2"  style="background-color: ${e.features[0].properties.stroke}"></div>
                <p class="text-white mb-0 mr-3" style="font-size: 1rem">${district}</p>
            </div>
          </div>
        </div>
      `;

        this.popup.setLngLat(e.lngLat).setHTML(popupContainer).addTo(this.map);
      }
    },
    tripMouseEnterAction(e) {
      this.map.getCanvas().style.cursor = "pointer";
    },
    tripMouseLeaveAction() {
      this.map.getCanvas().style.cursor = "";
    },
    async readURL(URL, name_source) {
      let arraysbound = [];
      try {
        const response = await axios.get(URL);
        if (response.data) {
          const features = response.data.features.map((item) => {
            arraysbound.push(...item.geometry.coordinates);
            let bounds = new mapboxgl.LngLatBounds();

            arraysbound.forEach((c) => {
              bounds.extend(c);
            });

            this.map.fitBounds(bounds, {
              padding: 25,
              easing: (t) => {
                return t;
              },
            });
            this.map.on("click", name_source, this.tripMouseClickAction);
            this.map.on("mouseenter", name_source, this.tripMouseEnterAction);
            this.map.on("mouseleave", name_source, this.tripMouseLeaveAction);
            return {
              ...item,
              properties: {
                ...item.properties,
                stroke:
                  !item.properties && !item.properties.stroke
                    ? "rgb(255,255,255)"
                    : item.properties.stroke,
              },
            };
          });

          this.map.addSource(name_source, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features,
            },
          });

          this.map.addLayer({
            id: name_source,
            type: "line",
            source: name_source,
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": ["get", "stroke"],
              "line-width": ["get", "stroke-width"],
              "line-opacity": 0.6,
            },
          });
        }
      } catch (err) {
        console.error("Failed to load GeoJSON for", name_source, err);
      }
    },
    drawline() {
      this.listOfTrams.forEach((element, index) => {
        const name_source = `trams-${element.type}-${
          element.year
        }-${element.district.name.replace(" ", "-")}-${element.id}`;

        this.readURL(element.geojson.url, name_source);
      });
    },
    async initMap() {
      return new Promise((resolve) => {
        mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_KEY;
        this.map = new mapboxgl.Map({
          container: "map",
          style: "mapbox://styles/mapbox/dark-v11",
          center: [101.414161, 3.320954],
          zoom: 9,
        });

        this.map.on("load", async () => {
          const geocoder = new MapboxGeocoder({
            accessToken: process.env.VUE_APP_MAPBOX_KEY,
            zoom: 12,
            limit: 10,
            placeholder: "Enter search e.g. Shah Alam",
            mapboxgl: mapboxgl,
            marker: {
              color: "orange",
            },
          });
          geocoder.setCountries("MY");
          resolve();
        });
      });
    },
    onDocumentClick(e) {
      if (!this.isFilterOpen) return;
      if (this.isResizing) return;
      if (Date.now() - this.resizeEndTime < 200) return;
      const root = this.$refs.filterRoot;
      if (root && !root.contains(e.target)) {
        this.isFilterOpen = false;
      }
    },
  },
  async mounted() {
    await this.initMap();

    try {
      await this.fetchAvailability();
    } catch (e) {
      // error already surfaced via errorMessage banner
    }

    const sel = this.pickDefaultSelection();
    if (sel) {
      this.applySelection(sel);
      this.snapYearWindowTo(sel.year);
    } else {
      this.snapYearWindowToLatest();
    }

    if (this.availability) {
      this.$nextTick(() => {
        this.isFilterOpen = true;
      });
    }

    this.popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      offset: 25,
    });

    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.onResizeEnd);
    document.body.style.cursor = "";
    clearTimeout(this.refreshTimer);
    if (this.tramsAbortController) this.tramsAbortController.abort();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
};
</script>

<style scoped>
.trams-filter {
  z-index: 5;
}
.trams-filter-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 10;
  width: max-content;
  max-width: calc(100vw - 3rem);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.trams-matrix-container {
  display: flex;
  align-items: stretch;
  gap: 4px;
  user-select: none;
}
.trams-districts-col {
  display: flex;
  flex-direction: column;
}
.trams-cell {
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: #fff;
  box-sizing: border-box;
}
.trams-header-cell {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
}
.trams-district-label {
  justify-content: flex-start;
  padding: 0 14px 0 4px;
  white-space: nowrap;
}
.trams-chevron-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  border-radius: 4px;
  width: 26px;
  padding: 0;
  align-self: stretch;
}
.trams-chevron-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}
.trams-chevron-btn:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.35);
}
.trams-year-viewport {
  overflow: hidden;
}
.trams-year-scroller {
  display: flex;
  transition: transform 0.25s ease-out;
  will-change: transform;
}
.trams-year-col {
  flex: 0 0 64px;
  display: flex;
  flex-direction: column;
}
.year-cell {
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
}
.year-cell.available {
  cursor: pointer;
  transition: background 0.15s ease;
}
.year-cell.available:hover {
  background: rgba(255, 255, 255, 0.1);
}
.year-cell.selected {
  background: rgba(13, 110, 253, 0.28);
  outline: 1px solid #0d6efd;
}
.dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
}
.cell-check {
  color: #0d6efd;
  font-size: 0.95rem;
}
.trams-help-icon {
  color: rgba(255, 255, 255, 0.45);
  cursor: help;
  font-size: 1rem;
  transition: color 0.15s ease;
}
.trams-help-icon:hover {
  color: rgba(255, 255, 255, 0.9);
}
.trams-resize-handle {
  width: 6px;
  align-self: stretch;
  cursor: ew-resize;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  transition: background 0.15s ease;
  margin-left: 2px;
}
.trams-resize-handle:hover,
.trams-resize-handle.resizing {
  background: rgba(13, 110, 253, 0.6);
}
.trams-error-banner,
.trams-empty-banner {
  position: absolute;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  max-width: 92vw;
}
</style>
